// OverviewSection — minimap-baserad workspace-overview som visas i
// PropertiesPanel för authenticated users. Verbatim extraherad ur
// PropertiesPanel.tsx (#8, POST_QA_SUBPLAN Wave E.1 step 4).
//
// State stannar i PropertiesPanel (minimapMode + setMinimapMode);
// alla Minimap-deps + capability-gating passas in via props.

import { Map } from 'lucide-react';
import { Minimap } from '@/components/editor/hud/Minimap';
import { MinimapModeToggle } from '@/components/editor/hud/minimap/MinimapModeToggle';
import { useTranslation } from '@/i18n/LanguageContext';
import type { WorkflowBlock, Connector, BlockGroup, BlockDimensions } from '@/types/workflow';
import type { MinimapMode } from '@/types/minimap';
import type { MinimapSearchProps } from '@/hooks/useMinimapSearchProps';

interface OverviewSectionProps {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  zoom: number;
  blockDimensions: Record<string, BlockDimensions>;
  onMinimapNavigate: (x: number, y: number) => void;
  onNavigateToGroup?: (groupId: string | null) => void;
  /** True när tenant-tier låter usern växla mellan overview-modes
   *  (search/refs/heatmap m.fl.) — döljer mode-toggle annars. */
  canShowAdvancedOverlays: boolean;
  minimapMode: MinimapMode;
  onMinimapModeChange: (next: MinimapMode) => void;
  /** Spread-able search-overlay-props (query, matches, …). */
  minimapSearchProps: MinimapSearchProps;
  /** Peer-cursors via realtime — bara för advanced-overlay-tiers. */
  peerCursors?: unknown;
}

export function OverviewSection({
  blocks, connectors, groups,
  canvasWidth, canvasHeight, viewportWidth, viewportHeight,
  scrollLeft, scrollTop, zoom, blockDimensions,
  onMinimapNavigate, onNavigateToGroup,
  canShowAdvancedOverlays, minimapMode, onMinimapModeChange,
  minimapSearchProps, peerCursors,
}: OverviewSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="p-5">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Map className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">{t('overview')}</h2>
        </div>
        {canShowAdvancedOverlays && (
          <MinimapModeToggle mode={minimapMode} onChange={onMinimapModeChange} />
        )}
      </div>
      <Minimap
        blocks={blocks}
        connectors={connectors}
        groups={groups}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        viewportWidth={viewportWidth}
        viewportHeight={viewportHeight}
        scrollLeft={scrollLeft}
        scrollTop={scrollTop}
        zoom={zoom}
        blockDimensions={blockDimensions}
        onNavigate={onMinimapNavigate}
        onNavigateToGroup={onNavigateToGroup}
        mode={canShowAdvancedOverlays ? minimapMode : 'normal'}
        {...minimapSearchProps}
        peerCursors={canShowAdvancedOverlays ? (peerCursors as never) : undefined}
        embedded
      />
    </div>
  );
}
